import { useState, useRef, useEffect } from 'react';
import { ChevronDown, GraduationCap } from 'lucide-react';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { useTrainerBatches } from '@/lib/hooks/useTrainer';
import { cn } from '@/lib/cn';

export function TrainerBatchSwitcher() {
  const { data: batches = [] } = useTrainerBatches();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const batchIdMatch = location.pathname.match(/\/trainer\/batches\/([^/]+)/);
  const activeBatchId = batchIdMatch?.[1] ?? null;
  const activeBatch = batches.find((b) => b.id === activeBatchId) ?? null;

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  const single = batches.length <= 1;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        disabled={single}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-1.5 text-xs font-semibold disabled:cursor-default"
      >
        <GraduationCap className="h-3.5 w-3.5 text-primary" />
        {activeBatch ? activeBatch.name : 'My Batches'}
        {!single && <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />}
      </button>
      {open && (
        <div role="menu" className="absolute left-0 z-20 mt-1 min-w-[200px] overflow-hidden rounded-lg border border-border bg-popover shadow-lg">
          {batches.map((b) => (
            <button
              key={b.id}
              role="menuitem"
              onClick={() => {
                navigate({ to: '/trainer/batches/$batchId/overview', params: { batchId: b.id } });
                setOpen(false);
              }}
              className={cn(
                'flex w-full items-center px-3 py-2 text-left text-sm hover:bg-muted',
                b.id === activeBatchId && 'font-semibold text-primary',
              )}
            >
              {b.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
